// packages/studio/web/src/diff/DiffDiagnostics.tsx
//
// Shows resolution errors/warnings for the active package's proposed state, so
// a reviewer can see "this proposal doesn't compile" instead of silently
// reading a half-resolved board. Renders nothing when the package resolves
// cleanly.

import { useState, type ReactElement } from "react";
import type { DiffDiagnostic } from "./useGitDiff.js";
import { cx } from "../lib/classNames.js";
import { useContextMenu } from "../ui/ContextMenu";
import { useTranslation } from "../i18n";

function locOf(d: DiffDiagnostic): string {
  const f = d.source?.file;
  const line = d.source?.start?.line;
  if (!f) return "";
  const bare = f.replace(/^[a-z]+:\/+/, "").replace(/^\/+/, "");
  return line ? `${bare}:${line}` : bare;
}

export function DiffDiagnostics({
  diagnostics,
}: {
  readonly diagnostics?: readonly DiffDiagnostic[];
}): ReactElement | null {
  const { t } = useTranslation("diff");
  const menu = useContextMenu();
  const [open, setOpen] = useState(false);
  const diags = diagnostics ?? [];
  if (diags.length === 0) return null;

  const errors = diags.filter((d) => d.severity === "error").length;
  const warnings = diags.length - errors;
  const tone = errors > 0 ? "error" : "warning";

  return (
    <div
      role="alert"
      className={cx(
        "border-b text-xs",
        tone === "error"
          ? "border-red-900/60 bg-red-950/40 text-red-200"
          : "border-amber-900/60 bg-amber-950/30 text-amber-200",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left"
      >
        <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>
        <span className="font-semibold">
          {errors > 0 ? t("diagnostics.errors", { count: errors }) : ""}
          {errors > 0 && warnings > 0 ? t("diagnostics.separator") : ""}
          {warnings > 0 ? t("diagnostics.warnings", { count: warnings }) : ""}
        </span>
        <span className="opacity-70">{t("diagnostics.unresolved")}</span>
        <svg className={cx("ml-auto h-3 w-3 shrink-0 transition-transform", open && "rotate-90")} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <ul className="max-h-48 overflow-auto px-3 pb-2">
          {diags.map((d, i) => {
            const loc = locOf(d);
            return (
              <li
                key={i}
                className="flex gap-2 py-0.5 font-mono text-[11px]"
                onContextMenu={(e) =>
                  menu.open(e, [
                    {
                      label: t("menu.copyMessage"),
                      onClick: () => void navigator.clipboard?.writeText(d.message),
                    },
                    ...(loc
                      ? [
                          {
                            label: t("menu.copyLocation"),
                            onClick: () => void navigator.clipboard?.writeText(loc),
                          },
                        ]
                      : []),
                  ])
                }
              >
                <span className={cx("shrink-0 font-semibold uppercase", d.severity === "error" ? "text-red-400" : "text-amber-400")}>
                  {d.severity}
                </span>
                {loc ? <span className="shrink-0 opacity-60">{loc}</span> : null}
                <span className="opacity-90">{d.message}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {menu.node}
    </div>
  );
}
